/**
 * The SA per-door v1 repair CONTRACT (LOCKER-343, Deliverable B).
 *
 * Lives in `shared/` because both sides of the wire need the same table, and the two
 * places it must agree are far apart: the backend decides the class and refuses a
 * mismatched action, and the drawer has to label the button and decide whether to
 * demand a second, explicit confirmation. A duplicated union would drift, and the
 * drift would be silent in the direction that matters — the FE offering an action the
 * BE rejects is a broken button, but the FE mislabelling class B as reversible is a
 * customer's reservation closed by accident.
 *
 * The letters are the ticket's own, so the UI, the audit record, the metric and the
 * ticket all say the same word.
 */

export const DoorDivergenceClassEnum = {
  /** Partner holds the door; we have no row. Door 3 / F6. */
  A: "A",
  /** Our row is terminal while v1 still holds the door. Door 1 / F5. IRREVERSIBLE. */
  B: "B",
  /** The partner's reservation id differs from our row's. */
  C: "C",
  /** Our row carries V2 door values on a v1 locker. F2 residue. */
  D: "D",
  /** Our v1 row carries `gsiSstRespk`. F3 residue. */
  E: "E",
  /** Our row is live; the partner has no reservation on this door. Report only. */
  F: "F",
} as const;

export type DoorDivergenceClass = keyof typeof DoorDivergenceClassEnum;

export const DoorDivergenceClassList = Object.values(
  DoorDivergenceClassEnum,
) as DoorDivergenceClass[];

export type DoorRepairAction =
  /** Class A — create the Reservation Thing from the v1 payload. */
  | "materializeReservation"
  /** Class B — write a terminal `CLOSE` to SST v1. IRREVERSIBLE. */
  | "closeOnPartner"
  /** Class C — repoint `_id`, `number` and `gsi1sk`. */
  | "repointReservationId"
  /** Class D — restore the v1 door values onto our row. */
  | "repairDoorFields"
  /** Class E — REMOVE `gsiSstRespk` from a v1 row. */
  | "removeGlobalIndexKey";

/**
 * Which action each class offers. ONE class, ONE action — deliberately not a menu.
 *
 * A menu would mean an operator choosing between repairs for a diagnosis they did not
 * make, and it is what would let a class-A door be "fixed" with a partner `CLOSE`.
 *
 * `F` maps to `null` on purpose. The partner says the door is free and our row says
 * otherwise; which side is wrong is not decidable from a single door, and both
 * candidate repairs are worse than telling a human — deleting our row is refused
 * outright by `reservationApi.isValidDelete`, and creating a partner reservation would
 * invent a parcel.
 */
export const ACTION_FOR_CLASS: Record<
  DoorDivergenceClass,
  DoorRepairAction | null
> = {
  A: "materializeReservation",
  B: "closeOnPartner",
  C: "repointReservationId",
  D: "repairDoorFields",
  E: "removeGlobalIndexKey",
  F: null,
};

/**
 * Classes whose apply cannot be undone on EITHER system.
 *
 * Only B. v1 `CLOSE` is terminal — `scripts/sstRestoreV1LockerReservations.ts` exists
 * because it cannot be walked back — so the UI must demand a second, explicit
 * confirmation for it and must say so in words.
 *
 * Class A is *not* here even though a materialised Reservation Thing cannot be deleted
 * (`isValidDelete` returns `unsupported`): it creates a row for a parcel the partner
 * says is physically present, so the worst case is a row that should not exist rather
 * than a door released while a parcel is still behind it.
 */
export const IRREVERSIBLE_CLASSES: DoorDivergenceClass[] = ["B"];

/** i18n key for a class's label, kept next to the table so neither drifts alone. */
export const doorDivergenceClassKey = (cls: DoorDivergenceClass): string =>
  `saLockerConsole.repair.class.${cls}`;
